import React, { useState } from "react";
import { ThemeProvider } from "onnit-ui/lib/themes/styled";
import GlobalStyle from "onnit-ui/lib/themes/GlobalStyle";
import theme from "onnit-ui/lib/themes/light";
import Box from "onnit-ui/lib/box/Box";
import Text, { List, ListItem, Sup } from "onnit-ui/lib/text";
import useOnnitContext from "onnit-ui/lib/hooks/useOnnitContext";
import usePageData from "../../hooks/usePageData";
import SupplementFacts from "../../components/common/SupplementFacts";
import { Img, Picture } from "onnit-ui/lib/image";
import ProductImageGrid from "onnit-ui/lib/module/ProductImageGrid";
import Grid, { TwelveColumn } from "onnit-ui/lib/module/grid/12/Grid";
import ImageContentPortrait from "onnit-ui/lib/module/ImageContentPortrait";
import CopyDisplay02 from "onnit-ui/lib/module/CopyDisplay02";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import Embed from "onnit-ui/lib/video/Embed";
import { Testimonial } from "onnit-ui/lib/module/testimonials";
import Product from "../../interfaces/Product";
import CustomPackageBuyBox from "onnit-ui/lib/buybox/simple/CustomPackageBuyBox";
import MediaContentShowcase from "onnit-ui/lib/module/MediaContentShowcase";
import keyIngredients from "./key-ingredients";


function Page() {
    const onnitContext = useOnnitContext();
    const { pageData, findProduct } = usePageData();
    //used to set current products supplement facts image.
    const [currentPID, setCurrentPID] = useState<number | undefined>();


    if (!pageData || !onnitContext) return null;

    const IMG_PARAMS = "?q=40&fm=pjpg&auto=compress,format";
    const IMG_URL = onnitContext.url.cdn.static_images + "/product-page/joint-oil/remediation";

    const orange = "#F5A800";

    const jointOilId = 3548;
    const shroomTechSport28Id = 4448;
    const shroomTechSport84Id = 4449;
    const peachId = 9124;
    const tigerId = 9125;
    const krillOilId = 322;
    const creatineId = 5587;

    const jointOil = findProduct(jointOilId) as Product;
    const creatine = findProduct(creatineId) as Product;
    const shroomTechSport28 = findProduct(shroomTechSport28Id) as Product;
    const shroomTechSport84 = findProduct(shroomTechSport84Id) as Product;
    const krillOil = findProduct(krillOilId) as Product;
    const peach = findProduct(peachId) as Product;
    const tiger = findProduct(tigerId) as Product;

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid variant="layout3">
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/Gallery-1-920x920.png${IMG_PARAMS}`}
                            alt="Join Oil bottle"
                            width={720}
                            height={720}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/Gallery-2-920x920.png${IMG_PARAMS}`}
                            alt="Juice oranges"
                            width={928}
                            height={928}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={494}
                            src={[
                                `${IMG_URL}/Gallery-3-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/Gallery-3-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="People doing joint exercises"
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/Gallery-4-920x920.png${IMG_PARAMS}`}
                            alt="Person stretching"
                            width={928}
                            height={928}
                        />
                        <Img
                            lazyLoad={false}
                            src={`${IMG_URL}/Gallery-5-920x920.png${IMG_PARAMS}`}
                            alt="Woman stretching her neck"
                            width={920}
                            height={920}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box backgroundColor="black" py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.3" typeStyle="text02">
                                    JOINT Oil
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Embed
                                    title="JOINT Oil product video"
                                    src="https://player.vimeo.com/video/856443669"
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                    pt={[6, 6, 0]}
                                >
                                    JOINT Oil
                                </Text>

                                <Text as="h2" color={orange} typeStyle="title01" mb={2}>
                                    Elbow (Knee and Shoulder) Grease
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 6" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" typeStyle="text02" color="onGray">
                                    High-impact activities like weight training, martial arts, running, and other sports
                                    can put a beating on your joints. Onnit’s Joint Oil provides 290mg of
                                    omega-3 and 160mg of omega-6 fats from different sources, along with turmeric root extract,
                                    to offer post-workout joint support and help maintain joint health over the long haul.
                                    <Sup />
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn py={[6, 6, 6, 8]} display={["block", "block", "grid"]} backgroundColor="black">
                            <Box gridColumn="3 / span 7" px={4}>
                                <Text as="h2" color={orange} typeStyle="title01" mb={2}>
                                    JOINT Oil Is For:
                                </Text>
                                <List mt={[4, 6]}>
                                    <ListItem typeStyle="text02" color="onGray">
                                        Athletes
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="onGray">
                                        Joggers
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="onGray">
                                        Recreational lifters
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="onGray">
                                        Active people age 40 and up
                                    </ListItem>
                                </List>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <TwelveColumn display={["block", "grid", "grid"]} mt={[6, 4, 6, 8]} justifyContent="center" alignItems="center">
                        <Box gridColumn={["1/-1", "2 / span 10", "2 / span 10"]} as="h2">
                            <Box maxWidth={[380, 400, 700, 900]} mx={["none", "auto"]} px={[4, 0]}>
                                <Text typeStyle="title01" mb={2} textAlign={["left", "center"]}>
                                    Delicious Tangerine Dream Flavored
                                </Text>
                                <Text as="p" fontSize={[2, 2, 4]} textAlign={["left", "center"]} color="#6e6e6e">
                                    With Other Natural Flavors
                                </Text>
                            </Box>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn display={["block", "grid", "grid"]} mt={[4, 4, 6]} justifyContent="center" alignItems="center" alignContent="center" mb={6}>
                        <Box gridColumn={["1/-1", "3 / span 3", "2 / span 4", "4 / span 2"]} my={4} width={224} mx="auto">
                            <Img
                                src={`${IMG_URL}/Flavor-224x135.png${IMG_PARAMS}`}
                                alt="Orange Juice."
                            />
                        </Box>

                        <Box gridColumn={["1/-1", "5 / span 6", "6 /span 5"]}>
                            <Text as="p" fontSize={3} color="#2f2f2f" px={[4, 4, 0]}>
                                For the best results, we recommend taking Joint Oil daily. To that end, we emulsified
                                it so it mixes easily in water and can be added to your favorite drink. Unlike many
                                other fish oil supplements, it doesn't taste like fish. The sweet citrusy flavor makes
                                it easy—even enjoyable—to drink.
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <Box bg="black">
                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box pt={[6, 6, 6, 8]} gridColumn={["1/-1", "1/-1", "2 / span 10"]}>
                                <CopyDisplay02 pt={[0, 0, 8]} pb={[6, 6, 8]} dashColor="#F38333">
                                    <Text as="h2" typeStyle="copyDisplay02" color="onGray" mb={4}>
                                        Can I Stack Joint Oil with Other Onnit Products?
                                    </Text>
                                    <Text as="p" typeStyle="text02" color="onGray" maxWidth="840px">
                                        Yes! Joint Oil can provide additional support when paired with Shroom Tech
                                        <Sup variant="reg" /> SPORT, Alpha BRAIN
                                        <Sup variant="reg" /> Pre-Workout, Krill Oil, and Creatine.
                                    </Text>
                                </CopyDisplay02>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <Box
                        py={[6, 6, 6, 8]}
                        display={["block", "block", "grid"]}
                        gridTemplateColumns="1fr 1fr"
                        maxWidth="1040px"
                        mx="auto"
                    >
                        <CustomPackageBuyBox
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Joint Oil + Shroom Tech SPORT
                                    <Sup variant="reg" />
                                </>
                            }
                            imgAltText="Joint Oil with Shroom Tech® SPORT."
                            selectableProducts={[shroomTechSport28, shroomTechSport84]}
                            packageProducts={[jointOil] as Product[]}
                            images={{
                                [shroomTechSport28Id]: `${IMG_URL}/Image-Stack-STS28.png${IMG_PARAMS}`,
                                [shroomTechSport84Id]: `${IMG_URL}/Image-Stack-STS84.png${IMG_PARAMS}`,
                            }}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Promotes joint health + aerobic endurance, and exercise volume.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Joint Oil + Alpha BRAIN
                                    <Sup variant="reg" /> Pre-Workout
                                </>
                            }
                            imgAltText="Joint Oil with Alpha BRAIN®."
                            selectableProducts={[peach, tiger]}
                            packageProducts={[jointOil] as Product[]}
                            images={{
                                [peachId]: `${IMG_URL}/Image-Stack-ABPW-YP.png${IMG_PARAMS}`,
                                [tigerId]: `${IMG_URL}/Image-Stack-ABPW-TB.png${IMG_PARAMS}`,
                            }}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Supports joint mobility + power, and endurance.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Joint Oil + Krill Oil
                                </>
                            }
                            imgAltText="Joint Oil with Krill Oil."
                            selectableProducts={[krillOil]}
                            packageProducts={[jointOil] as Product[]}
                            images={{
                                [krillOilId]: `${IMG_URL}/Image-Stack-KRILL.png${IMG_PARAMS}`,
                            }}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Assists with joint function + helps maintain cardiovascular health.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>

                        <CustomPackageBuyBox
                            gridColumn="span 1"
                            m={7}
                            name={
                                <>
                                    Joint Oil + Creatine
                                </>
                            }
                            imgAltText="Joint Oil with Creatine."
                            selectableProducts={[creatine]}
                            packageProducts={[jointOil] as Product[]}
                            images={{
                                [creatineId]: `${IMG_URL}/Image-Stack-CREATINE.png${IMG_PARAMS}`,
                            }}
                        >
                            <List>
                                <ListItem color="grays.5">
                                    Promotes joint function + exercise performance, and lean muscle gain.
                                    <Sup />
                                </ListItem>
                            </List>
                        </CustomPackageBuyBox>
                    </Box>


                    <Box py={[6, 6, 6, 8]} mx="auto" display="flex" justifyContent="center">
                        <Testimonial
                            imgSrc={`${IMG_URL}/testimonial-aubrey.png`}
                            quote={`"I haven’t been this excited about an Onnit product in years. I have been using 
                                JOINT Oil consistently since the first prototype and have noticed undeniable benefits to 
                                my joint health and overall performance. Not only that, the taste is so good that I actually 
                                look forward to taking it, which is truly the first time I can say that for any fish oil."`}
                            author="Aubrey Marcus | Onnit Founder"
                        />
                    </Box>

                    <Box bg="black" py={[6, 6, 6, 8]}>
                        <Box>
                            <Grid mb={4}>
                                <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 12"]} pt={3} px={[4, 4, 4, 0]} mb={4}>
                                    <Text role="heading" aria-level={2} as="p" typeStyle="copyDisplay02" color="white">
                                        Key Ingredients
                                    </Text>
                                </Box>
                            </Grid>

                            <MediaContentShowcase
                                mb={6}
                                items={keyIngredients.map((x) => {
                                    let copy = { ...x };
                                    copy.image = `${IMG_URL}/${x.image}`;
                                    return copy;
                                })}
                                textColor="#757575"
                                activeColor="white"
                                activeTextColor="grays.3"
                                bgMedia={
                                    <Img
                                        placeholder={[754, 208]}
                                        src={`${IMG_URL}/Ingredient-Background.png?q=75&auto=format`}
                                        alt="Key ingredients background"
                                        aria-hidden="true"
                                    />
                                }
                            />
                        </Box>
                    </Box>

                    <Box pt={6}>
                        <ImageContentPortrait
                            reverse={true}
                            image={
                                <Img
                                    alt="Gamer in his room"
                                    placeholder={[464, 600]}
                                    src={`${IMG_URL}/How.png?q=40`}
                                    width={926}
                                    height={1200}
                                />
                            }
                            content={
                                <Box display="flex" flexDirection="column" height="100%" justifyContent="center" px={[1]} ml={["auto", "auto", "auto", -5]}>
                                    <Text as="h2" typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                        How Joint Oil Works
                                    </Text>
                                    <Text as="p" typeStyle="text02" fontWeight="regular" mb={[4, 4, 5]}>
                                        A well-balanced diet supplemented with omega-3 fats, EPA and DHA, and turmeric extract
                                        nourishes the lining of the joints, from the shoulders and elbows to the hips, knees,
                                        and ankles.<Sup variant="dagger" />
                                    </Text>
                                </Box>
                            }
                        />
                    </Box>
                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers
                        supplementFacts={<SupplementFacts currentPID={currentPID} />}
                    >
                        <Box>
                            <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" mb={4}>
                                Refrigerate after opening.
                            </Text>
                            <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" mb={4}>
                                SHAKE WELL BEFORE EACH USE
                            </Text>
                            <Text as="p" fontSize={0} color="grays.4" fontWeight="heavy" display="inline" mr={1}>
                                WARNING:
                            </Text>
                            <Text as="p" fontSize={0} display="inline" typeStyle="default" color="grays.4" mb={4}>
                                Consult a medical doctor before taking this or any other nutritional supplement if you are pregnant,
                                nursing, have or suspect a medical condition or are taking any medications. Store in a cool,
                                dry place.
                            </Text>
                            <Text as="p" fontSize={0} typeStyle="default" color="grays.4" mt={4} mb={4} fontWeight="black">
                                KEEP OUT OF REACH OF CHILDREN.
                            </Text>
                            <Text as="p" fontSize={0} color="grays.4" fontWeight={"bold"} mb={4}>
                                AvoVida
                                <Sup variant="reg" /> is a registered trademark of Cyvex Nutrition, Inc.
                            </Text>
                            <Img
                                lazyLoad={false}
                                src={`${IMG_URL}/sugar_gluten_free.png${IMG_PARAMS}`}
                                alt="Sugar and Gluten Free"
                                style={{ maxWidth: "224px" }}
                                mb={4}
                            />
                            <Text as="p" fontSize={0} color="grays.4" fontWeight={"bold"} mb={4}>
                                NOT A LOW CALORIE SUPPLEMENT
                            </Text>
                        </Box>
                    </UsageAndDisclaimers>
                )}
                <TwelveColumn display={["block", "block", "grid"]}>
                    <Box gridColumn={["1/-1", "1 / span 4", "1 / span 7", "2 / span 4"]} px={[5, 0, 5, 0]}>
                        <Text as="p" fontSize={1} fontWeight="regular" textTransform="uppercase" mb={4} color="grays.6">
                            References
                        </Text>
                        <Text as="p" fontSize={1} fontWeight="regular" mb={4} color="grays.6">
                            1. Flock, M.R., Harris, W.S. and Kris-Etherton, P.M., 2013. Long-chain omega-3 fatty acids:
                            time to establish a dietary reference intake.{" "}<Text as="span" color="grays.6" display="inline" fontStyle="italic" fontSize={1}>Nutrition reviews</Text>,
                            71(10), pp.692-707.
                        </Text>
                    </Box>
                </TwelveColumn>
            </>
        </ThemeProvider>
    );
}

export default Page;